import React from 'react'

import { connect } from 'react-redux'
import styled from 'styled-components'

import { getCookie, setCookie } from '@helpers'

import { IconButton, Paper, Typography } from '@components/ui'
import { XmarkRegularIcon } from '@components/ui/svgIcons'
import { AnnouncementIcon } from '@oldComponents/ui'

import { ANNOUNCEMENT_COOKIE_PREFIX } from '@constants'

const MIN_WIDTH_BREAKPOINT = 551

const CloseIconButton = styled(IconButton)`
  position: absolute;
  top: 10px;
  right: 10px;
`

const TitleTypography = styled(Typography)`
  margin-bottom: 10px;

  @media (min-width: ${MIN_WIDTH_BREAKPOINT}px) {
    margin-right: 25px;
  }
`

const DescriptionTypography = styled(Typography)`
  & > a {
    color: inherit;
    font-weight: 600;
    text-decoration: underline;
  }
`

const Wrapper = styled.div`
  position: fixed;
  z-index: ${({ theme }) => theme.zIndex.announcement};
  right: 25px;
  bottom: 25px;
  left: 20px;
  height: auto;

  @media (min-width: ${MIN_WIDTH_BREAKPOINT}px) {
    left: auto;
    width: 500px;
    min-height: 140px;
  }
`

const StyledPaper = styled(Paper)`
  @media (min-width: ${MIN_WIDTH_BREAKPOINT}px) {
    display: flex;
  }
`

const IconContainer = styled.div`
  background-color: ${({ theme }) => theme.colors.gray[10]};
  padding: 5px 10px;

  & > svg {
    display: block;
  }

  @media (max-width: ${MIN_WIDTH_BREAKPOINT - 1}px) {
    border-bottom-width: 1px;
    border-bottom-style: solid;
    border-bottom-color: ${({ theme }) => theme.colors.gray[102]};

    & > * {
      width: 25px;
      height: 25px;
    }
  }

  @media (min-width: ${MIN_WIDTH_BREAKPOINT}px) {
    min-width: 140px;
    min-height: 140px;
    border-right-width: 1px;
    border-right-style: solid;
    border-right-color: ${({ theme }) => theme.colors.common.paperStroke};
    padding: 30px;
  }
`

const Content = styled.div`
  padding: 10px;
  padding-bottom: 20px;

  @media (min-width: ${MIN_WIDTH_BREAKPOINT}px) {
    padding-bottom: 10px;
  }
`

function isAnnouncementVisible(cookieName: string, showOnce: boolean) {
  const cookie = getCookie(cookieName)
  if (cookie && showOnce) {
    return false
  }
  return true
}

function buildLink(text: string): React.ReactNode[] {
  const re = /<link:url=([^<]+)>([^<]+)<\/link>/gi
  const parts: React.ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null

  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index).replace(/<\/?link(:url=[^<>]+)?>/gi, ''))
    }
    parts.push(
      <a key={match.index} href={match[1]} target="_blank" rel="noopener noreferrer">
        {match[2]}
      </a>
    )
    lastIndex = re.lastIndex
  }

  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex).replace(/<\/?link(:url=[^<>]+)?>/gi, ''))
  }

  return parts
}

export interface AnnouncementProps {
  announcement: Announcement
}

export function Announcement({
  announcement: { id, title, description, show_once: showOnce, to_time: expDate },
}: AnnouncementProps) {
  const cookieName = `${ANNOUNCEMENT_COOKIE_PREFIX}_${id}`
  const [visible, setVisible] = React.useState(isAnnouncementVisible(cookieName, showOnce))

  const handleHide = React.useCallback(() => {
    if (showOnce) {
      const expiration = expDate ? new Date(expDate) : 365 // default 365 days
      setCookie(cookieName, 1, expiration)
    }
    setVisible(false)
  }, [showOnce, expDate, cookieName])

  if (!visible) {
    return null
  }

  return (
    <Wrapper>
      <StyledPaper elevation={2}>
        <IconContainer>
          <AnnouncementIcon />
        </IconContainer>
        <Content>
          <CloseIconButton size="small" variant="secondaryText" onClick={handleHide}>
            <XmarkRegularIcon size={9} />
          </CloseIconButton>
          <TitleTypography tag="h5" size="400-md">
            {title}
          </TitleTypography>
          <DescriptionTypography size="400-xs">
            <span>{buildLink(description)}</span>
          </DescriptionTypography>
        </Content>
      </StyledPaper>
    </Wrapper>
  )
}

interface DashboardAnnouncementProps {
  announcement: Nullable<Announcement>
}

function PureDashboardAnnouncement({ announcement }: DashboardAnnouncementProps) {
  if (!announcement) {
    return null
  }

  return <Announcement announcement={announcement} />
}

export const DashboardAnnouncement = connect((state: Store) => ({
  announcement: state.auth.company.data?.announcement,
}))(PureDashboardAnnouncement)

DashboardAnnouncement.displayName = 'DashboardAnnouncement'
